import React, { useEffect, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import moment from "moment/moment";
import { IoMdArrowBack } from "react-icons/io";
import Loader from "../../components/loader/loader";
import { getBillingTransactions } from "../../api/context/billing";

const TYPE_META = {
  trial_grant: { label: "Trial", color: "#16a34a", positive: true },
  admin_grant: { label: "Admin Grant", color: "#8b5cf6", positive: true },
  topup_purchase: { label: "Top-up", color: "#16a34a", positive: true },
  topup_consume: { label: "Consumed", color: "#dc2626", positive: false },
  subscription_consume: { label: "Consumed", color: "#dc2626", positive: false },
  subscription_reset: { label: "Renewed", color: "#2563eb", positive: true },
  refund: { label: "Refund", color: "#16a34a", positive: true },
};

const BillingHistory = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user")) || {};
  const isVendor = user?.userType === "Vendor";

  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isVendor) return;
    setLoading(true);
    getBillingTransactions({ page, pageSize })
      .then((res) => {
        if (res?.data?.success) {
          const data = res.data.data;
          const list = Array.isArray(data)
            ? data
            : data?.transactions || data?.rows || [];
          const total = Array.isArray(data)
            ? list.length
            : data?.pagination?.total ||
              data?.totalItems ||
              data?.total ||
              list.length;
          setRows(list);
          setTotalRows(total);
        }
      })
      .finally(() => setLoading(false));
  }, [isVendor, page, pageSize]);

  if (!isVendor) {
    return (
      <div className="p-4 text-center text-muted">
        Billing is only available on vendor accounts.
      </div>
    );
  }

  const columns = [
    {
      name: "Date",
      selector: (r) => (r.createdAt ? moment(r.createdAt).format("MMM D, YYYY HH:mm") : "-"),
      center: true,
      grow: 1,
    },
    {
      name: "Type",
      cell: (r) => {
        const meta = TYPE_META[r.type] || { label: r.type, color: "#6c757d" };
        return (
          <span
            className="billing-txn-badge"
            style={{ background: `${meta.color}1a`, color: meta.color }}
          >
            {meta.label}
          </span>
        );
      },
      center: true,
      grow: 1,
    },
    {
      name: "Description",
      selector: (r) => r.description || "-",
      center: true,
      grow: 1,
      wrap: true,
    },
    {
      name: "Change",
      cell: (r) => {
        const meta = TYPE_META[r.type];
        const sign = meta?.positive ? "+" : "-";
        return (
          <span
            style={{
              fontWeight: 600,
              color: meta?.color || "#6b7280",
            }}
          >
            {sign}
            {Math.abs(r.amount)}
          </span>
        );
      },
      center: true,
      grow: 1,
    },
    {
      name: "Balance",
      selector: (r) => r.balanceAfter ?? "-",
      center: true,
      grow: 1,
    },
  ];

  return (
    <div>
      <Row className="mb-4">
        <Col>
          <div className="page-header-sticky page-title-box d-flex align-items-center mb-0">
            <div
              onClick={() => navigate("/billing")}
              style={{
                cursor: "pointer",
                fontSize: 24,
                fontWeight: 800,
                display: "flex",
                alignItems: "center",
              }}
            >
              <IoMdArrowBack className="icon" />
            </div>
            <h4 className="mb-0 fw-bold">Transaction history</h4>
          </div>
        </Col>
      </Row>

      <Card className="shadow-sm">
        <Card.Body className="p-0">
          <DataTable
            columns={columns}
            data={rows}
            progressPending={loading}
            progressComponent={<div style={{ height: "50vh" }}><Loader /></div>}
            pagination
            paginationServer
            paginationTotalRows={totalRows}
            paginationPerPage={pageSize}
            paginationRowsPerPageOptions={[10, 20, 50, 100]}
            onChangePage={setPage}
            onChangeRowsPerPage={(newSize) => {
              setPageSize(newSize);
              setPage(1);
            }}
            currentPage={page}
            paginationDefaultPage={page}
          />
        </Card.Body>
      </Card>
    </div>
  );
};

export default BillingHistory;
